import type { Draft } from "../types.js";

/**
 * Time + draft-title/preview helpers shared across the marketing surfaces,
 * extracted from `marketing/shared.tsx`. All pure (display-only — nothing here
 * mutates a stored draft) so they unit-test directly.
 */

export function minutesAgo(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
}

export function minutesUntil(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60_000));
}

/**
 * Human-readable age — "just now" / "12m ago" / "3h ago" / "5d ago". Replaces
 * the raw "1540m old" minutes display that read as nonsense for older drafts.
 */
export function humanAge(iso: string): string {
  const mins = minutesAgo(iso);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// ── Draft title + preview helpers ───────────────────────────────────
//
// Drafts almost always arrive with `title: null` (the agent writes the
// post body, not a headline) and a body written in the same Markdown the
// review dialog renders. Two display problems fell out of that:
//   1. every untitled draft showed the literal "Untitled draft", and
//   2. list/preview surfaces sliced the raw body, leaking `**bold**`
//      markers as plain text.
// These helpers are the single source of truth for "what title do we
// SHOW" + "plain-text projection of the body", so the review dialog, the
// pending-approval list, and the Create preview all agree. Display only —
// nothing here mutates the stored draft.

/** Longest auto-derived title we'll show before truncating with an ellipsis. */
const DERIVED_TITLE_MAX = 80;

/**
 * Strip the Markdown markers we actually render (heading hashes, block
 * quotes, list bullets, links/images, **bold**, *italic*, `code`) from a
 * chunk of text so it reads as plain prose. This is intentionally a
 * lightweight projection for titles/snippets — NOT a parser; the dialog
 * body still goes through the real `<Markdown>` renderer.
 */
function stripMarkdownMarkers(text: string): string {
  return text
    .replace(/^\s{0,3}#{1,6}\s+/, "") // leading heading hashes
    .replace(/^\s*>\s?/, "") // blockquote marker
    .replace(/^\s*[-*+]\s+/, "") // unordered bullet
    .replace(/^\s*\d+\.\s+/, "") // ordered marker
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links / images → text
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold
    .replace(/(\*|_)(.*?)\1/g, "$2") // italic
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/[*_`#>]/g, "") // any stray markers
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Plain-text projection of a draft body: every line stripped of Markdown
 * markers and joined with spaces. For list rows / clamped previews where
 * we can't (and shouldn't) render full JSX but must not leak `**`.
 */
export function draftBodyPreview(body: string): string {
  return body
    .split("\n")
    .map((line) => stripMarkdownMarkers(line))
    .filter((line) => line.length > 0)
    .join(" ")
    .trim();
}

/**
 * Derive a human title from the first meaningful line of the body —
 * Markdown stripped, collapsed, and truncated at a word boundary. Returns
 * null when the body has no usable text (caller falls back to a
 * placeholder).
 */
export function deriveTitleFromBody(body: string): string | null {
  const firstLine = body
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (!firstLine) return null;
  const clean = stripMarkdownMarkers(firstLine);
  if (!clean) return null;
  if (clean.length <= DERIVED_TITLE_MAX) return clean;
  const cut = clean.slice(0, DERIVED_TITLE_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  const trimmed = (lastSpace > DERIVED_TITLE_MAX / 2 ? cut.slice(0, lastSpace) : cut).trimEnd();
  return `${trimmed}…`;
}

/**
 * The title to SHOW for a draft: its explicit title when set, otherwise
 * one auto-derived from the body, otherwise the "Untitled draft"
 * placeholder. Single source of truth so the review dialog header, the
 * queue list, and the Create preview all label a draft the same way.
 */
export function draftDisplayTitle(draft: Pick<Draft, "title" | "body">): string {
  const explicit = draft.title?.trim();
  if (explicit) return explicit;
  return deriveTitleFromBody(draft.body) ?? "Untitled draft";
}
